import React, { useEffect, useState, useRef } from "react";

function toHex(input) {
  if (!input) return null;
  let c = String(input).trim();

  // #RGB ili #RRGGBB
  let m = c.match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (m) {
    let h = m[1];
    if (h.length === 3) h = h.split("").map(x => x + x).join("");
    return "#" + h.toUpperCase();
  }

  // rgb(...) ili "r,g,b" / "r g b" / "r;g;b" / "r|g|b"
  const parts = c
    .replace(/rgb\(|\)/gi, "")
    .split(/[\s,;|]+/)
    .map(s => s.trim())
    .filter(Boolean);

  if (parts.length === 3 && parts.every(p => /^\d+$/.test(p))) {
    const nums = parts.map(n => Math.max(0, Math.min(255, parseInt(n, 10))));
    return (
      "#" +
      nums.map(n => n.toString(16).padStart(2, "0")).join("").toUpperCase()
    );
  }

  return null; // neprepoznat format
}

function textOn(hex) {
  try {
    const h = hex.replace("#", "");
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    const L = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return L > 160 ? "#111" : "#fff";
  } catch {
    return "#111";
  }
}

export default function ProductDecorForm({ initial, decor, onSave, onCancel }) {
  const src = initial ?? decor ?? {};
  const EMPTY = {
    id: "",
    DecorCode: "",
    Structure: "",
    URL: "",
    Texture: 0,
    ManufacturerName: "",
    ColorAndCharacter: "",
    AverageRGB: "#999999",
    hasImage: false,
    imageUrl: null,
  };

  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [imgError, setImgError] = useState(false);
  const [averaging, setAveraging] = useState(false);

  // napuni formu kada stigne rekord
  useEffect(() => {
    const next = {
      ...EMPTY,
      ...src,
      Texture: Number(src?.Texture) ? 1 : 0,
      AverageRGB: src?.AverageRGB || "#999999",
    };
    setForm(next);
    setFile(null);
    setPreview(null);
    setImgError(false);
  }, [src]);

  // local preview iz File input-a
  useEffect(() => {
    if (!file) { setPreview(null); return; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const serverBase = "https://modularobjects.com/products";
  const existingImageUrl =
    form.id ? `${serverBase}/api/products/decor/${encodeURIComponent(form.id)}/image` : null;

  const imgSrc = preview || (form.id ? `${existingImageUrl}?ts=${Date.now()}` : null);
  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    const payload = {
      id: form.id || undefined,
      DecorCode: form.DecorCode || null,
      Structure: form.Structure || null,
      URL: form.URL || null,
      Texture: form.Texture ? 1 : 0,
      ManufacturerName: form.ManufacturerName || null,
      ColorAndCharacter: form.ColorAndCharacter || null,
      AverageRGB: form.AverageRGB || null,
    };
    onSave?.(payload, file);
  };

  // ======= AVERAGE COLOR (front) =======
  async function averageFromCurrentImage() {
    try {
      setAveraging(true);
      setImgError(false);

      // 1) ako imamo local preview (blob:), to je najbolje (nema CORS)
      if (preview) {
        const hex = await averageFromUrl(preview, { isLocalBlob: true });
        if (hex) update("AverageRGB", hex);
        setAveraging(false);
        return;
      }

      // 2) pokušaj sa postojećom slikom sa servera
      if (existingImageUrl) {
        const hex = await averageFromUrl(existingImageUrl, { withCacheBust: true });
        if (hex) update("AverageRGB", hex);
      }
    } catch (e) {
      console.error("average color failed:", e);
    } finally {
      setAveraging(false);
    }
  }

  /**
   * Učitaj sliku i izračunaj prosečnu boju preko canvas-a.
   * - za remote slike koristimo crossOrigin='anonymous'
   * - za local blob nije potrebno
   */
  function averageFromUrl(url, opts = {}) {
    const { isLocalBlob = false, withCacheBust = false } = opts;
    return new Promise((resolve, reject) => {
      const img = new Image();
      if (!isLocalBlob) img.crossOrigin = "anonymous";
      img.onload = () => {
        try {
          const hex = averageFromImageElement(img);
          resolve(hex);
        } catch (err) {
          reject(err);
        }
      };
      img.onerror = reject;
      img.src = withCacheBust ? `${url}${url.includes("?") ? "&" : "?"}cb=${Date.now()}` : url;
    });
  }

  function averageFromImageElement(imgEl) {
    // downscale radi brzine
    const w = 64, h = Math.max(1, Math.round((imgEl.height / imgEl.width) * 64)) || 64;
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(imgEl, 0, 0, w, h);

    const { data } = ctx.getImageData(0, 0, w, h);
    let r = 0, g = 0, b = 0, n = 0;

    // saberi sve piksele (bez alpha)
    for (let i = 0; i < data.length; i += 4) {
      const a = data[i + 3];
      if (a === 0) continue; // providni preskoči
      r += data[i];
      g += data[i + 1];
      b += data[i + 2];
      n++;
    }
    if (!n) return "#999999";
    r = Math.round(r / n);
    g = Math.round(g / n);
    b = Math.round(b / n);
    return (
      "#" +
      [r, g, b]
        .map(v => Math.max(0, Math.min(255, v)).toString(16).padStart(2, "0"))
        .join("")
        .toUpperCase()
    );
  }
  // =====================================

  return (
    <div className="bg-white dark:bg-neutral-800 p-4 rounded shadow">
      <h3 className="text-xl font-semibold mb-4">
        {form.id ? "Edit decoration" : "Create decoration"}
      </h3>

      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* IMAGE */}
          <div>
            <div className="mb-2 font-medium">Image</div>
            <div className="w-28 h-28 border rounded overflow-hidden flex items-center justify-center bg-neutral-50">
              {imgSrc && !imgError ? (
                <img
                  key={imgSrc}
                  src={imgSrc}
                  alt="decor"
                  className="w-full h-full object-cover"
                  crossOrigin="anonymous"
                  onError={() => setImgError(true)}
                />
              ) : (
                <span className="text-sm text-gray-500">No image</span>
              )}
            </div>

            <input
              type="file"
              accept="image/*"
              className="mt-2 block w-full text-sm"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />

            <button
              type="button"
              onClick={averageFromCurrentImage}
              disabled={averaging || (!preview && !existingImageUrl)}
              className="mt-2 px-3 py-1.5 rounded border text-sm hover:bg-gray-50 disabled:opacity-50"
              title="Izračunaj srednju boju iz slike i upiši u AverageRGB"
            >
              {averaging ? "Calculating…" : "Set average from image"}
            </button>
          </div>

          {/* FIELDS */}
          <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="ID" disabled value={form.id} onChange={(v) => update("id", v)} />
            <Field label="Decor Code" required value={form.DecorCode} onChange={(v) => update("DecorCode", v)} />
            <Field label="Manufacturer" value={form.ManufacturerName} onChange={(v) => update("ManufacturerName", v)} />
            <Field label="Structure" value={form.Structure} onChange={(v) => update("Structure", v)} />
            <Field label="Color / Character" value={form.ColorAndCharacter} onChange={(v) => update("ColorAndCharacter", v)} />
            <Field label="URL" value={form.URL} onChange={(v) => update("URL", v)} />

            {/* Texture (dugme/toggle) */}
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <label className="block text-sm mb-1">Texture</label>
                <div className="flex items-center gap-2">
                  <span className="text-sm">{form.Texture ? "Yes" : "No"}</span>
                  <button
                    type="button"
                    className="px-3 py-1.5 rounded border text-sm hover:bg-gray-50"
                    onClick={() => update("Texture", form.Texture ? 0 : 1)}
                  >
                    Toggle
                  </button>
                </div>
              </div>
            </div>

            {/* Average color sa validacijom + swatch */}
            <div>
              <label className="block text-sm mb-1">Average Color</label>
              {(() => {
                const hex = toHex(form.AverageRGB) || "#999999";
                const invalid = !toHex(form.AverageRGB);
                return (
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={hex}
                      onChange={(e) => update("AverageRGB", e.target.value.toUpperCase())}
                      className="h-10 w-16 p-0 border rounded"
                      title={form.AverageRGB || ""}
                    />
                    <input
                      type="text"
                      value={form.AverageRGB}
                      onChange={(e) => update("AverageRGB", e.target.value)}
                      className={`w-32 px-2 py-2 border rounded dark:bg-neutral-700 dark:text-white ${
                        invalid ? "border-red-500" : ""
                      }`}
                      placeholder="#AABBCC ili 153,162,140"
                    />
                    <span
                      className="text-xs px-2 py-1 rounded border"
                      style={{ backgroundColor: hex, color: textOn(hex) }}
                    >
                      {hex}
                    </span>
                    {invalid && <span className="text-xs text-red-500">Nepoznat format</span>}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <button type="button" onClick={onCancel} className="px-4 py-2 rounded border">Cancel</button>
          <button type="submit" className="bg-primary-light text-white px-4 py-2 rounded hover:bg-primary-dark">Save</button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, required, disabled }) {
  return (
    <div>
      <label className="block text-sm mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        disabled={disabled}
        required={required}
        value={value ?? ""}
        onChange={(e) => onChange?.(e.target.value)}
        className="w-full px-3 py-2 border rounded dark:bg-neutral-700 dark:text-white disabled:opacity-60"
      />
    </div>
  );
}
